import { SearchDimenionsResponse } from "@depmap/types";
import {
  fetchDimensionIdentifiers,
  fetchDatasetIdentifiers,
  fetchDimensionTypeDisplayName,
  fetchDatasetName,
} from "./api-helpers";

export async function fetchDataTypeCompatibleIds(
  slice_type: string,
  dataType: string | null
) {
  let ids = await fetchDimensionIdentifiers(slice_type, dataType);

  if (ids.length === 0) {
    ids = await fetchDimensionIdentifiers(slice_type);
  }

  return new Set(ids.map(({ id }) => id));
}

export async function fetchDataVersionCompatibleIds(
  slice_type: string,
  dataset_id: string | null
) {
  if (!dataset_id) {
    return null;
  }

  const ids = await fetchDatasetIdentifiers(slice_type, dataset_id);
  return new Set(ids.map(({ id }) => id));
}

const chainLength = (str: string) => str.split(".").length;

function computeDisabledState(
  id: string,
  dataTypeCompatibleIds: Set<string>,
  dataVersionCompatibleIds: Set<string> | null,
  dataType: string | null,
  dimensionTypeDisplayName: string,
  datasetName: string
) {
  if (!dataTypeCompatibleIds.has(id)) {
    return {
      isDisabled: true,
      disabledReason: [
        `The data type "${dataType}"`,
        "has no data versions with this",
        dimensionTypeDisplayName,
      ].join(" "),
    };
  }

  if (dataVersionCompatibleIds && !dataVersionCompatibleIds.has(id)) {
    return {
      isDisabled: true,
      disabledReason: [
        "The data version",
        `"${datasetName}"`,
        "doesn’t include this",
        dimensionTypeDisplayName,
      ].join(" "),
    };
  }

  return { isDisabled: false, disabledReason: "" };
}

/**
 * Determines whether a single, already-known identifier (typically the
 * component's current `value`) is compatible with the given data type /
 * dataset. Unlike `convertSearchResultToOptions`, this doesn't require the
 * identifier to be present in a (possibly truncated) options list, so it's
 * safe to use for validating a value even when `defaultOptions` only
 * contains a small sample of all identifiers.
 */
export async function checkValueCompatibility(
  id: string,
  slice_type: string,
  dataType: string | null,
  dataset_id: string | null
) {
  const [
    dataTypeCompatibleIds,
    dataVersionCompatibleIds,
    dimensionTypeDisplayName,
    datasetName,
  ] = await Promise.all([
    fetchDataTypeCompatibleIds(slice_type, dataType),
    fetchDataVersionCompatibleIds(slice_type, dataset_id),
    fetchDimensionTypeDisplayName(slice_type),
    fetchDatasetName(dataset_id),
  ]);

  return computeDisabledState(
    id,
    dataTypeCompatibleIds,
    dataVersionCompatibleIds,
    dataType,
    dimensionTypeDisplayName,
    datasetName
  );
}

async function convertSearchResultToOptions(
  tokens: string[],
  result: SearchDimenionsResponse,
  slice_type: string,
  dataType: string | null,
  dataset_id: string | null
) {
  const asyncData = await (() => {
    return Promise.all([
      fetchDataTypeCompatibleIds(slice_type, dataType),
      fetchDataVersionCompatibleIds(slice_type, dataset_id),
      fetchDimensionTypeDisplayName(slice_type),
      fetchDatasetName(dataset_id),
    ]).catch((e) => {
      window.console.error(e);
    });
  })();

  if (!asyncData) {
    return [];
  }

  const [
    dataTypeCompatibleIds,
    dataVersionCompatibleIds,
    dimensionTypeDisplayName,
    datasetName,
  ] = asyncData;

  return result
    .map(({ id, label, matching_properties }) => {
      const { isDisabled, disabledReason } = computeDisabledState(
        id,
        dataTypeCompatibleIds,
        dataVersionCompatibleIds,
        dataType,
        dimensionTypeDisplayName,
        datasetName
      );

      const groupedProps: Record<string, Set<string>> = {};

      const tokenMatches = tokens
        .map((token) => {
          const lowercaseToken = token.toLowerCase();

          return matching_properties
            .filter(({ property }) => property !== "label")
            .filter(({ value }) => {
              return value.toLowerCase().includes(lowercaseToken);
            })
            .sort((a, b) => {
              return chainLength(a.property) - chainLength(b.property);
            })[0];
        })
        .filter(Boolean);

      tokenMatches.forEach(({ property, value }) => {
        const prop = property
          // no underscores
          .replace(/_/g, " ")
          // capitalize "ID"
          .replace(/\bids?\b/gi, "ID")
          // Never use "label" as a nested property (it doesn't give the user
          // any real information)
          .replace(/\.label$/, "")
          // Now return only the last member of the property chain (this
          // assumes that's the most meaningful info we can show the user).
          .split(".")
          .slice(-1)[0];

        groupedProps[prop] ||= new Set();
        groupedProps[prop].add(value);
      });

      const nonLabelProperties = Object.keys(groupedProps)
        .filter((property) => property !== "label")
        .map((property) => ({
          property,
          values: [...groupedProps[property]],
        }));

      return {
        value: id,
        label,
        isDisabled,
        disabledReason,
        nonLabelProperties,
      };
    })
    .sort((a, b) => {
      if (a.isDisabled && !b.isDisabled) {
        return 1;
      }

      if (!a.isDisabled && b.isDisabled) {
        return -1;
      }

      const labelA = (
        a.nonLabelProperties[0]?.values[0] || a.label
      )?.toLowerCase();

      const labelB = (
        b.nonLabelProperties[0]?.values[0] || b.label
      )?.toLowerCase();

      if (tokens.length === 0) {
        return labelA < labelB ? -1 : 1;
      }

      const indexA = tokens.reduce(
        (sum, token) => labelA.indexOf(token) + sum,
        0
      );
      const indexB = tokens.reduce(
        (sum, token) => labelB.indexOf(token) + sum,
        0
      );

      if (indexA === indexB) {
        return labelA < labelB ? -1 : 1;
      }

      return indexA < indexB ? -1 : 1;
    });
}

export default convertSearchResultToOptions;
